import React, { useState, useEffect } from 'react';
import { Info, X, Calendar, GitBranch, Package, GitCommit, FileText } from 'lucide-react';
import { useDarkMode } from '../DarkModeContext';

/**
 * VersionBadge Component
 * Displays current version with commit info from Git
 */
const VersionBadge = () => {
  const { darkMode } = useDarkMode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);

  // Fetch version info from generated JSON file
  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now());
        if (response.ok) {
          const data = await response.json();
          setVersionInfo(data);
        }
      } catch (error) {
        console.log('Version info not available:', error.message);
      }
    };
    fetchVersionInfo();
  }, []);

  // Fallback version if file not loaded
  const currentVersion = versionInfo || {
    version: '2.7.x',
    commitHash: '...',
    commitMessage: 'Loading...',
    formattedDate: 'Loading...',
    formattedTime: '',
    branch: 'main',
    commitCount: 0
  };

  const handleClose = (e) => {
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      {/* Version Badge - Compact, clickable */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs cursor-pointer transition-all hover:scale-105 ${
          darkMode
            ? 'bg-transparent hover:bg-white/10 border border-white/10'
            : 'bg-transparent hover:bg-gray-100 border border-gray-200'
        }`}
        title="Click for version details"
      >
        <Package size={12} className={darkMode ? 'text-white' : 'text-gray-700'} />
        <span className={`font-mono font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          v{currentVersion.version}
        </span>
        <span className={`font-mono font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
          {currentVersion.commitHash}
        </span>
        <Info size={10} className={darkMode ? 'text-white/70' : 'text-gray-400'} />
      </button>

      {/* Version Detail Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center pt-32 p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
          <div
            className={`w-full max-w-md overflow-hidden rounded-2xl shadow-2xl backdrop-blur-xl flex flex-col max-h-[80vh] overflow-y-auto ${
              darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b backdrop-blur-md ${
              darkMode
                ? 'bg-gradient-to-r from-red-900/90 via-rose-800/90 to-pink-900/90 border-gray-700'
                : 'bg-gradient-to-r from-red-600/90 via-rose-500/90 to-pink-600/90 border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                    <Package size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Version {currentVersion.version}
                    </h2>
                    <p className="text-sm text-red-100/80">
                      {currentVersion.formattedDate} {currentVersion.formattedTime && `• ${currentVersion.formattedTime}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsModalOpen(false);
                  }}
                  className="p-2 rounded-xl bg-white hover:bg-gray-100 transition-all shadow-lg hover:scale-110"
                  title="Fermer"
                >
                  <X size={20} className="text-gray-900" strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* Recent Commits History */}
            <div className={`px-6 py-4 border-b ${
              darkMode ? 'bg-cyan-900/40 border-cyan-700/50' : 'bg-cyan-50 border-cyan-200'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <FileText size={18} className={darkMode ? 'text-cyan-400' : 'text-cyan-600'} />
                <span className={`text-sm font-bold uppercase tracking-wide ${darkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                  Recent Changes
                </span>
              </div>
              <div className="space-y-3">
                {(currentVersion.recentCommits || [{ hash: currentVersion.commitHash, message: currentVersion.commitMessage, date: currentVersion.formattedDate, time: currentVersion.formattedTime }]).map((commit, index) => (
                  <div key={commit.hash} className={`flex gap-3 ${index > 0 ? `pt-3 border-t ${darkMode ? 'border-cyan-700/30' : 'border-cyan-200'}` : ''}`}>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0
                        ? 'bg-green-500 text-white'
                        : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {commit.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-mono ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          {commit.hash}
                        </span>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>•</span>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {commit.date} {commit.time}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Commit Info */}
              <div className={`p-4 rounded-xl border ${
                darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <GitCommit size={14} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                      <span className={`text-xs uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Commit Hash
                      </span>
                    </div>
                    <div className={`text-lg font-mono font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      {currentVersion.commitHash}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1 justify-end">
                      <GitBranch size={14} className={darkMode ? 'text-green-400' : 'text-green-600'} />
                      <span className={`text-xs uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Branch
                      </span>
                    </div>
                    <div className={`text-lg font-mono font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                      {currentVersion.branch}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-4 rounded-xl border text-center ${
                  darkMode ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {currentVersion.commitCount}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total Commits
                  </div>
                </div>
                <div className={`p-4 rounded-xl border text-center ${
                  darkMode ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'
                }`}>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    {currentVersion.version}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Version
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex flex-col items-center gap-3">
                <p className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Powered by HaliSoft
                </p>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-2 rounded-lg font-semibold transition-all bg-red-600 hover:bg-red-700 text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VersionBadge;
